import { useMemo } from 'react'
import { useConfig } from '@/shared/config/use-config'
import {
  deviceTimeZone,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatRelative,
  type FormatContext,
} from './format'
import { resolveLocale, useLocaleStore } from './locale-store'

/** The user's chosen language, and what the backend could actually serve. */
export function useLanguage() {
  const config = useConfig()
  const userChoice = useLocaleStore((state) => state.locale)
  const servedLanguage = useLocaleStore((state) => state.servedLanguage)
  const setLanguage = useLocaleStore((state) => state.setLocale)
  return { language: resolveLocale(userChoice, config.locale), servedLanguage, setLanguage }
}

/** Formatters bound to the active language and the timezone of this device. */
export function useFormatters() {
  const { language: locale } = useLanguage()

  return useMemo(() => {
    const ctx: FormatContext = { locale, timeZone: deviceTimeZone() }
    return {
      dateTime: (value: string | number | Date, pattern?: string) =>
        formatDateTime(value, ctx, pattern),
      relative: (value: string | number | Date, now?: Date) => formatRelative(value, ctx, now),
      number: (value: number, options?: Intl.NumberFormatOptions) =>
        formatNumber(value, ctx, options),
      percent: (ratio: number, digits?: number) => formatPercent(ratio, ctx, digits),
    }
  }, [locale])
}
